import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@sr-test-wizard/shared';
import { gameKbApi } from '../../lib/gameKbApi';
import { useGameKbStore } from '../../state/gameKbStore';
import { MarkdownView } from './ReportView';

interface Props {
  runId: string;
}

/**
 * Dedicated chat for the Game Knowledge Base. Hits `/game-kb/chat` and is
 * grounded server-side in the generated reports + image/video analysis +
 * live file tools. The "tracing the package" line reflects the WS
 * `game-kb-progress` chat stream so the tester sees which files the model
 * is opening while it answers.
 */
export function GameChat({ runId }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const chatProgress = useGameKbStore((s) => s.progress.chat);

  useEffect(() => {
    gameKbApi
      .listChat(runId)
      .then((items) => setMessages(items ?? []))
      .catch(() => setMessages([]));
  }, [runId]);

  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight });
  }, [messages.length, busy]);

  const send = async () => {
    const question = draft.trim();
    if (!question || busy) return;
    setBusy(true);
    setError(null);
    setDraft('');
    const optimistic: ChatMessage = {
      id: -Date.now(),
      role: 'user',
      content: question,
      ts: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      const data = await gameKbApi.sendChat(runId, question);
      setMessages((prev) => [...prev, data.message]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Clear this game chat history?')) return;
    try {
      await gameKbApi.resetChat(runId);
      setMessages([]);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const tracing = busy && chatProgress?.status === 'progress' && chatProgress.tool;

  return (
    <div className="chat">
      <div className="row" style={{ margin: '0 0 8px', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Ask about this game</h3>
        <span className="spacer" />
        {messages.length > 0 && (
          <button type="button" className="btn btn-ghost small" onClick={() => void reset()}>
            Reset
          </button>
        )}
      </div>
      <div ref={feedRef} className="messages">
        {messages.length === 0 && (
          <p className="muted small">
            Ask anything about the game&apos;s features, math, payouts, RTP variants, bonus
            mechanics, or code restrictions. The assistant is grounded in the generated reports
            and can trace any file in the source package - including non-critical files - to
            verify its answer.
          </p>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`msg ${msg.role}`}>
            <MarkdownView>{msg.content}</MarkdownView>
          </div>
        ))}
        {busy && (
          <div className="msg assistant">
            <span className="muted small">
              {tracing ? `Tracing the package… ${chatProgress?.tool}` : 'Thinking…'}
            </span>
          </div>
        )}
      </div>
      {error && <p style={{ color: 'var(--bad)' }}>{error}</p>}
      <div className="composer">
        <textarea
          value={draft}
          placeholder="e.g. How is the free spins feature triggered, and is the picker a real choice or predetermined?"
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button className="btn btn-primary" disabled={busy || !draft.trim()} onClick={() => void send()}>
          {busy ? 'Sending...' : 'Send'}
        </button>
      </div>
      <p className="muted small" style={{ margin: '6px 0 0' }}>
        Press Ctrl/Cmd + Enter to send.
      </p>
    </div>
  );
}
